import Head from 'next/head'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useRouter } from 'next/router'
import { RadioGroup } from '@headlessui/react'
import {
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon as XMarkIconMini,
} from '@heroicons/react/20/solid'
import { FAQ } from '@/components/UI/FAQ'
import { classNames } from '@/utils/helpers/classNames'
import { PricingPlan } from '@/components/UI/PricingPlan'
import { Skeleton, SkeletonCard } from '@/components/common/Skeleton'
import { useProduct, PRODUCTS_CACHE_KEY, PRODUCTS_CACHE_TTL_MS } from '../../context/ProductContext'
import { StripeHelper } from 'stripeHelper'
import { saveCachedData, getCachedData } from '@/utils/helpers/localStorageHelper'
import { ObjectHelper } from '@/utils/helpers/objectHelper'
import { buildFallbackPricingData, getPublicPlanSummary, resolveTrialDays } from '@/constants/PlanMarketing'
import { trackFunnelEvent } from '@/utils/analytics/funnelTracking'

export default function Pricing() {
  const router = useRouter()
  const [productData, setProductData] = useState(null)
  const [frequency, setFrequency] = useState(null)
  const [pricingError, setPricingError] = useState(null)
  const [isRetrying, setIsRetrying] = useState(false)
  const viewedRef = useRef(false)
  const { transformProductData, getProducts } = useProduct()

  const fetchData = useCallback(async ({ force = false } = {}) => {
    setIsRetrying(force)
    setPricingError(null)

    try {
      let activeProducts = !force
        ? getCachedData(PRODUCTS_CACHE_KEY, PRODUCTS_CACHE_TTL_MS)
        : null
      let source = activeProducts && !ObjectHelper.isObjectEmpty(activeProducts) ? 'cache' : 'stripe'

      if (ObjectHelper.isObjectEmpty(activeProducts)) {
        try {
          activeProducts = await StripeHelper.listActiveProducts()
          if (activeProducts?.length) saveCachedData(PRODUCTS_CACHE_KEY, activeProducts)
        } catch (stripeError) {
          source = 'firestore'
          activeProducts = await getProducts()
          if (!activeProducts?.length) throw stripeError
        }
      }

      const transformed = await transformProductData(activeProducts)
      if (!transformed?.tiers?.length) throw new Error('No paid plans are currently available.')

      const tiers = transformed.tiers.map((tier) => {
        const summary = getPublicPlanSummary(tier.name)
        const hasLiveMonthlyPrice = Boolean(tier?.priceIds?.monthly) && Number.isFinite(Number(tier?.price?.monthly))
        return {
          ...tier,
          price: {
            ...tier.price,
            monthly: hasLiveMonthlyPrice
              ? tier.price.monthly
              : Number(summary?.monthlyPrice).toFixed(2),
          },
          trialDays: resolveTrialDays(tier?.trialDays, summary?.trialDays ?? 0),
          trialTermsVerified: true,
          pricingFallback: !hasLiveMonthlyPrice,
        }
      })
      const hasFallbackTiers = tiers.some((tier) => tier.pricingFallback)
      const distinctTrialDays = new Set(tiers.map((tier) => (
        tier.trialTermsVerified ? tier.trialDays : null
      )))
      const sharedTrialDays = distinctTrialDays.size === 1
        ? tiers[0]?.trialDays
        : null
      const pageContent = sharedTrialDays > 0
        ? {
            ...transformed.pageContent,
            trialHeadline: `Try any plan free for ${sharedTrialDays} days`,
            trialDescription: `For first-time subscribers. A payment method is required; cancel before your ${sharedTrialDays}-day trial ends and you won’t be charged.`,
          }
        : {
            ...transformed.pageContent,
            trialHeadline: 'Clear terms before you subscribe',
            trialDescription: 'Trial availability is shown on each plan. Stripe confirms the exact amount and charge date before you complete Checkout.',
          }
      setProductData({
        ...transformed,
        tiers,
        pageContent,
        pricingFallback: hasFallbackTiers,
      })
      if (hasFallbackTiers) {
        setPricingError('Some live prices could not be confirmed. Display prices are shown for comparison and the final amount will be confirmed before Checkout.')
      }
      trackFunnelEvent('pricing_loaded', {
        source,
        tier_count: tiers.length,
        has_fallback_tiers: hasFallbackTiers,
      })
    } catch (error) {
      const fallback = buildFallbackPricingData()
      setProductData({
        ...fallback,
        pageContent: {
          ...fallback.pageContent,
          trialHeadline: 'Clear terms before you subscribe',
          trialDescription: 'Live billing terms are temporarily unavailable. Stripe confirms the exact trial, amount, and charge date before you complete Checkout.',
        },
      })
      setPricingError('We could not confirm live billing prices. You can review the plans below, retry, and your final price will always be confirmed before Checkout.')
      trackFunnelEvent('pricing_load_failed', {
        error: error?.message || 'unknown_error',
        fallback_rendered: true,
      })
    } finally {
      setIsRetrying(false)
    }
  }, [getProducts, transformProductData])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (!router.isReady || viewedRef.current) return
    viewedRef.current = true
    trackFunnelEvent('pricing_viewed', {
      source: router.query.from || null,
    })
  }, [router.isReady, router.query.from])

  useEffect(() => {
    if (productData && productData.frequencies.length > 0) {
      setFrequency(productData.frequencies[0])
    }
  }, [productData])

  // Skeleton while Stripe products load (was a blank page — `return null`).
  if (!productData) {
    return (
      <main className="dark">
        <div className="hc-app min-h-screen bg-[#2F3437] text-white">
          <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
            <div className="mb-4 flex justify-center"><Skeleton className="h-12 w-2/3 max-w-xl" /></div>
            <div className="mb-12 flex justify-center"><Skeleton className="h-5 w-1/2 max-w-md" /></div>
            <div className="mx-auto grid max-w-md grid-cols-1 gap-6 md:max-w-2xl md:grid-cols-2 lg:max-w-none lg:grid-cols-3">
              {[0, 1, 2].map((i) => <SkeletonCard key={i} className="h-96" />)}
            </div>
          </div>
        </div>
      </main>
    )
  }

  // Desktop comparison layout: one label column + one column per tier on
  // sale (3 since Basic was retired). Literal classes for Tailwind's purge.
  const tierCount = productData.tiers.length
  const comparisonGridClass = { 2: 'grid-cols-3', 3: 'grid-cols-4' }[tierCount] || 'grid-cols-5'
  const comparisonColWidthClass = { 2: 'w-1/3', 3: 'w-1/4' }[tierCount] || 'w-1/5'

  return (
    <>
      <Head>
        <title>Plans & Pricing - OpenSpeech</title>
      </Head>
      <main className="dark">
        <div className="hc-app relative min-h-screen overflow-hidden bg-[#2F3437] text-white">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.75) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.75) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
          <div className="isolate overflow-hidden">
          <div className="relative z-10 flow-root py-16 lg:pb-0">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <div className="relative z-10">
                <h1 className="mx-auto max-w-4xl text-center text-4xl font-bold tracking-tight text-white sm:text-6xl">
                  {productData?.pageContent?.pageTitle || 'Pricing plans for teams of all sizes.'}
                </h1>
                <p className="mx-auto mt-6 max-w-2xl text-center leading-8 text-gray-300">
                  {productData?.pageContent?.pageSubtitle || 'Our pricing plans cater to teams of all sizes, making it easy for everyone to find a plan that fits their needs and budget.'}
                </p>
                <div className="mx-auto mt-5 max-w-xl rounded-2xl border border-indigo-400/30 bg-indigo-500/10 px-4 py-3 text-center">
                  <p className="font-semibold text-indigo-100">
                    {productData?.pageContent?.trialHeadline}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-gray-300 sm:text-sm">
                    {productData?.pageContent?.trialDescription}
                  </p>
                </div>
                {pricingError ? (
                  <div className="mx-auto mt-4 flex max-w-2xl flex-col items-center justify-between gap-3 rounded-xl border border-amber-400/25 bg-amber-400/10 px-4 py-3 text-center text-sm text-amber-100 sm:flex-row sm:text-left" role="alert">
                    <span>{pricingError}</span>
                    <button
                      type="button"
                      onClick={() => fetchData({ force: true })}
                      disabled={isRetrying}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-amber-300/30 px-3 py-1.5 text-xs font-semibold text-amber-100 hover:bg-amber-300/10 disabled:cursor-wait disabled:opacity-60"
                    >
                      <ArrowPathIcon className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
                      {isRetrying ? 'Retrying…' : 'Retry live prices'}
                    </button>
                  </div>
                ) : null}
                {/* <div className="relative mt-16 flex justify-center">
                  <RadioGroup
                    value={frequency}
                    onChange={(option) => {
                      setFrequency(option)
                    }}
                    className="grid grid-cols-2 gap-x-1 rounded-full bg-white/5 p-1 text-center text-xs font-semibold leading-5 ring-1 ring-inset ring-gray-200"
                  >
                    <RadioGroup.Label className="sr-only">
                      Payment frequency
                    </RadioGroup.Label>
                    {productData.frequencies.map((option) => (
                      <RadioGroup.Option
                        key={option.value}
                        value={option}
                        className={({ checked }) =>
                          classNames(
                            checked ? 'bg-indigo-500 text-white' : '',
                            'cursor-pointer rounded-full px-2.5 py-1'
                          )
                        }
                      >
                        <span>{option.label}</span>
                      </RadioGroup.Option>
                    ))}
                  </RadioGroup>
                  <span className="absolute -mt-4 ml-36 inline-flex h-6 items-center rounded-full bg-green-500 px-2.5 py-0.5 text-xs font-medium text-white">
                    Save 20%
                  </span>
                </div> */}
              </div>
              <PricingPlan pricing={productData} />
            </div>
          </div>
          <div className="relative z-10 lg:pt-14">
            <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
              <section
                aria-labelledby="mobile-comparison-heading"
                className="lg:hidden"
              >
                <h2 id="mobile-comparison-heading" className="sr-only">
                  Feature comparison
                </h2>

                <div className="mx-auto max-w-2xl space-y-16">
                  {productData.tiers.map((tier) => (
                    <div key={tier.id} className="border-t border-gray-900/10">
                      <div
                        className={classNames(
                          tier.featured
                            ? 'border-indigo-600'
                            : 'border-transparent',
                          '-mt-px w-72 border-t-2 pt-10 md:w-80'
                        )}
                      >
                        <h3
                          className={classNames(
                            tier.featured ? 'text-indigo-600' : 'text-gray-900 dark:text-gray-100',
                            'text-sm font-semibold leading-6'
                          )}
                        >
                          {tier.name}
                        </h3>
                        <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">
                          {tier.description}
                        </p>
                      </div>

                      <div className="mt-10 space-y-10">
                        {productData.sections.map((section) => (
                          <div key={section.name}>
                            <h4 className="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-100">
                              {section.name}
                            </h4>
                            <div className="relative mt-6">
                              <div
                                aria-hidden="true"
                                className="absolute inset-y-0 right-0 hidden w-1/2 rounded-lg bg-white dark:bg-gray-900 shadow-sm sm:block"
                              />

                              <div
                                className={classNames(
                                  tier.featured
                                    ? 'ring-2 ring-indigo-600'
                                    : 'ring-1 ring-gray-900/10 dark:ring-white/10',
                                  'relative rounded-lg bg-white dark:bg-gray-900 shadow-sm sm:rounded-none sm:bg-transparent sm:shadow-none sm:ring-0'
                                )}
                              >
                                <dl className="divide-y divide-gray-200 text-sm leading-6">
                                  {section.features.map((feature) => (
                                    <div
                                      key={feature.name}
                                      className="flex items-center justify-between px-4 py-3 sm:grid sm:grid-cols-2 sm:px-0"
                                    >
                                      <dt className="pr-4 text-gray-600 dark:text-gray-400">
                                        {feature.name}
                                      </dt>
                                      <dd className="flex items-center justify-end sm:justify-center sm:px-4">
                                        {typeof feature.tiers[tier.name] ===
                                        'string' ? (
                                          <span
                                            className={
                                              tier.featured
                                                ? 'font-semibold text-indigo-600'
                                                : 'text-gray-900 dark:text-gray-100'
                                            }
                                          >
                                            {feature.tiers[tier.name]}
                                          </span>
                                        ) : (
                                          <>
                                            {feature.tiers[tier.name] ===
                                            true ? (
                                              <CheckIcon
                                                className="mx-auto h-5 w-5 text-indigo-600"
                                                aria-hidden="true"
                                              />
                                            ) : (
                                              <XMarkIconMini
                                                className="mx-auto h-5 w-5 text-gray-400"
                                                aria-hidden="true"
                                              />
                                            )}

                                            <span className="sr-only">
                                              {feature.tiers[tier.name] === true
                                                ? 'Yes'
                                                : 'No'}
                                            </span>
                                          </>
                                        )}
                                      </dd>
                                    </div>
                                  ))}
                                </dl>
                              </div>

                              {/* Fake card border */}
                              <div
                                aria-hidden="true"
                                className={classNames(
                                  tier.featured
                                    ? 'ring-2 ring-indigo-600'
                                    : 'ring-1 ring-gray-900/10 dark:ring-white/10',
                                  'pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 rounded-lg sm:block'
                                )}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section
                aria-labelledby="comparison-heading"
                className="hidden lg:block"
              >
                <h2 id="comparison-heading" className="sr-only">
                  Feature comparison
                </h2>

                <div className={classNames('grid gap-x-8 border-t border-gray-900/10 before:block', comparisonGridClass)}>
                  {productData.tiers.map((tier) => (
                    <div key={tier.id} aria-hidden="true" className="-mt-px">
                      <div
                        className={classNames(
                          tier.featured
                            ? 'border-indigo-600'
                            : 'border-transparent',
                          'border-t-2 pt-10'
                        )}
                      >
                        <p
                          className={classNames(
                            tier.featured ? 'text-indigo-600' : 'text-gray-900 dark:text-gray-100',
                            'text-sm font-semibold leading-6'
                          )}
                        >
                          {tier.name}
                        </p>
                        <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">
                          {tier.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="-mt-6 space-y-16">
                  {productData.sections.map((section) => (
                    <div key={section.name}>
                      <h3 className="relative z-10 text-sm font-semibold leading-6 text-gray-900 dark:text-gray-100">
                        {section.name}
                      </h3>
                      <div className="relative -mx-8 mt-10">
                        <div
                          className={classNames('absolute inset-x-8 inset-y-0 grid gap-x-8 before:block', comparisonGridClass)}
                          aria-hidden="true"
                        >
                          {productData.tiers.map((tier) => (
                            <div key={tier.id} className="h-full w-full rounded-lg bg-white dark:bg-gray-900 shadow-sm" />
                          ))}
                        </div>

                        <table className="relative w-full border-separate border-spacing-x-8">
                          <thead>
                            <tr className="text-left">
                              <th scope="col">
                                <span className="sr-only">Feature</span>
                              </th>
                              {productData.tiers.map((tier) => (
                                <th key={tier.id} scope="col">
                                  <span className="sr-only">
                                    {tier.name} tier
                                  </span>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {section.features.map((feature, featureIdx) => (
                              <tr key={feature.name}>
                                <th
                                  scope="row"
                                  className={classNames(comparisonColWidthClass, 'relative z-10 py-3 pr-4 text-left text-sm font-normal leading-6 text-gray-900 dark:text-gray-100')}
                                >
                                  {feature.name}
                                  {featureIdx !==
                                  section.features.length - 1 ? (
                                    <div className="absolute inset-x-8 mt-3 h-px bg-gray-200" />
                                  ) : null}
                                </th>
                                {productData.tiers.map((tier) => (
                                  <td
                                    key={tier.id}
                                    className={classNames('relative px-4 py-0 text-center', comparisonColWidthClass)}
                                  >
                                    <span className="relative h-full w-full py-3">
                                      {typeof feature.tiers[tier.name] ===
                                      'string' ? (
                                        <span
                                          className={classNames(
                                            tier.featured
                                              ? 'font-semibold text-indigo-600'
                                              : 'text-gray-900 dark:text-gray-100',
                                            'text-sm leading-6'
                                          )}
                                        >
                                          {feature.tiers[tier.name]}
                                        </span>
                                      ) : (
                                        <>
                                          {feature.tiers[tier.name] === true ? (
                                            <CheckIcon
                                              className="mx-auto h-5 w-5 text-indigo-600"
                                              aria-hidden="true"
                                            />
                                          ) : (
                                            <XMarkIconMini
                                              className="mx-auto h-5 w-5 text-gray-400"
                                              aria-hidden="true"
                                            />
                                          )}

                                          <span className="sr-only">
                                            {feature.tiers[tier.name] === true
                                              ? 'Yes'
                                              : 'No'}
                                          </span>
                                        </>
                                      )}
                                    </span>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div
                          className={classNames('pointer-events-none absolute inset-x-8 inset-y-0 grid gap-x-8 before:block', comparisonGridClass)}
                          aria-hidden="true"
                        >
                          {productData.tiers.map((tier) => (
                            <div
                              key={tier.id}
                              className={classNames(
                                tier.featured
                                  ? 'ring-2 ring-indigo-600'
                                  : 'ring-1 ring-gray-900/10 dark:ring-white/10',
                                'rounded-lg'
                              )}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>

          <div className="relative z-10">
            <FAQ />
          </div>
        </div>
      </main>
    </>
  )
}
